import { useMemo, useState, type ReactNode } from 'react'

import type { AiModule, ImprovementProposalCreate } from '../../../app/ai-os'
import { newIdempotencyKey } from '../../../app/ai-os'
import { checkValue } from '@npc/copy'

import { ApiFailure } from '../../../app/errors'
import { ALL_RISKS, riskRequiresHumanClick } from '../../../app/module-evolution'
import { Button } from '../../../components/button'
import { Modal } from '../../../components/modal'
import type { CopyId } from '../../../copy'
import { t } from '../../../copy'
import { T } from '../../../copy/text'
import { EMPTY_DRAFT, toInput, validate, type Draft } from './draft'
import { RiskTag } from './labels'
import { RISK_DETAIL } from './words'

/**
 * 提一件要改的事。**这一组唯一的写口。**
 *
 * # 它只做一件事：记下来
 *
 * 不接受、不执行、不派活、不碰生产。提交成功之后那句
 * `ai_os.action.submitted`（「记下了。要不要开工还得再说一步，我不会自己就动手。」）
 * 就是说这个的——少了它，用户会以为点完「记下来」这件事就开始做了。
 *
 * # 幂等键在弹窗打开那一刻生成一次
 *
 * 提交失败之后「再来一次」**原样带着同一个键**。别处几个 mutation 是在
 * `mutationFn` 里现生成的，那样一来每次重试都是一个新键——幂等成了摆设，
 * 而它失效的样子和正常一模一样。契约把代价写得很直白：
 * **一条重复的提案会真的派出去一次开发任务。**
 *
 * # 三种结局说三句话，因为下一步不一样
 *
 *   成功            `ai_os.action.submitted`     记下了，开工还得再说一步
 *   同键同载荷      `ai_os.conflict.already_submitted`  我已经记过一次了，没重复记
 *   同键不同载荷    `ai_os.conflict.same_key_different_content`
 *                                                 **重试同一个键还是 409**，
 *                                                 他要做的是重新开一条
 *
 * # 红级配「不用人点」是 400，界面上先说清
 *
 * 服务端不会「好心」帮你改成 true 然后放行。选到红的那一刻勾选框就锁死，
 * 并显示 `ai_os.field.requires_human_click.locked`。**只往严的方向调**——
 * 黄级也可以主动要求人点，那条路留着。
 */

export interface CreateProposalProps {
  modules: AiModule[]
  onSubmit: (vars: { input: ImprovementProposalCreate; idempotencyKey: string }) => void
  pending: boolean
  error?: unknown
  /** 上一次提交成功了吗（时间戳）。成功之后换一把新的幂等键。 */
  submittedAt?: number
  /** 服务端是不是回了「我已经记过一次了」。同键同载荷那一档。 */
  replayed?: boolean
}

export function CreateProposal({
  modules,
  onSubmit,
  pending,
  error,
  submittedAt,
  replayed = false,
}: CreateProposalProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [invalid, setInvalid] = useState<CopyId | null>(null)

  /**
   * 幂等键。**依赖是「这次编辑」而不是「这次提交」**——
   * `open` 与 `submittedAt` 变了才换一把，改字段不换。
   * 改一个字就换一把的话，「改一个错别字再提交」会变成新开一条提案。
   */
  const idempotencyKey = useMemo(
    () => newIdempotencyKey(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, submittedAt],
  )

  const conflict = error instanceof ApiFailure && error.status === 409
  // 服务端说这条已经不在这个项目里了——**与「一时读不到」不是一回事**：
  // 重试没有用，他要换一个模块
  const moduleGone =
    error instanceof ApiFailure && error.status === 404 && Boolean(draft.moduleId)

  return (
    <>
      <Button size="sm" variant="primary" data-testid="ai-os-create-open" onClick={() => setOpen(true)}>
        <T id="ai_os.action.create" />
      </Button>

      <Modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) {
            setDraft(EMPTY_DRAFT)
            setInvalid(null)
          }
        }}
        title={t('ai_os.action.create')}
        description={t('ai_os.action.submitted')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="ai-os-create-cancel">
              <T id="ai_os.action.cancel" />
            </Button>
            <Button
              variant="primary"
              disabled={pending}
              data-testid="ai-os-create-submit"
              onClick={() => {
                const bad = validate(draft)
                if (bad) {
                  setInvalid(bad)
                  return
                }
                setInvalid(null)
                onSubmit({ input: toInput(draft)!, idempotencyKey })
              }}
            >
              <T id={pending ? 'ai_os.action.submitting' : 'ai_os.action.submit'} />
            </Button>
          </>
        }
      >
        <form className="flex flex-col gap-npc-gap-lg" onSubmit={(e) => e.preventDefault()}>
          <Row label="ai_os.field.module">
            <select
              data-testid="ai-os-create-module"
              value={draft.moduleId}
              onChange={(e) => setDraft({ ...draft, moduleId: e.target.value })}
              className={FIELD}
            >
              <option value="" />
              {modules.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.display_name}
                </option>
              ))}
            </select>
          </Row>

          <Row label="ai_os.field.title">
            <input
              data-testid="ai-os-create-title"
              value={draft.title}
              maxLength={240}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className={FIELD}
            />
          </Row>

          <Row label="ai_os.field.goal">
            <textarea
              data-testid="ai-os-create-goal"
              value={draft.goal}
              rows={2}
              onChange={(e) => setDraft({ ...draft, goal: e.target.value })}
              className={FIELD}
            />
          </Row>

          <Row label="ai_os.field.user_value" hint="ai_os.field.user_value.hint">
            <textarea
              data-testid="ai-os-create-user-value"
              value={draft.userValue}
              rows={2}
              onChange={(e) => setDraft({ ...draft, userValue: e.target.value })}
              className={FIELD}
            />
          </Row>

          {/* 「影响的是谁」。契约第三轮把它加成必填，理由写在那一列上：
              **拿不到它，界面就只能自己编一个「影响谁」**——
              而 CLAUDE.md §3 要求每条告警都说清影响谁，编出来的那个数字
              最不该出现在那儿。

              ⚠️ **它没有自己的标签模板。** `ai_os.proposal.impact` 是
              「影响的是 {who}」——一句带变量的话，不是一个字段名，
              而 `ai_os.field.*` 那一批里没有对应的一条。已回报。

              在那之前不编一个字段名出来，改用**边填边显示那句话**：
              填「40 个下单的客人」，下面就长出「影响的是 40 个下单的客人」。
              那句话本身就说清了这个框要什么，而且它一个字都不是我造的。
              读屏用户走 `aria-describedby` 指到同一句上。 */}
          <ImpactField
            value={draft.impactAudience}
            onChange={(next) => setDraft({ ...draft, impactAudience: next })}
          />

          <Row label="ai_os.field.problem_evidence" hint="ai_os.field.problem_evidence.hint">
            <textarea
              data-testid="ai-os-create-evidence"
              value={draft.problemEvidence}
              rows={3}
              onChange={(e) => setDraft({ ...draft, problemEvidence: e.target.value })}
              className={MONO_FIELD}
            />
          </Row>

          <Row label="ai_os.field.acceptance" hint="ai_os.field.acceptance.hint">
            <textarea
              data-testid="ai-os-create-acceptance"
              value={draft.acceptance}
              rows={3}
              onChange={(e) => setDraft({ ...draft, acceptance: e.target.value })}
              className={MONO_FIELD}
            />
          </Row>

          <Row label="ai_os.field.stop_conditions" hint="ai_os.field.stop_conditions.hint">
            <textarea
              data-testid="ai-os-create-stop"
              value={draft.stopConditions}
              rows={3}
              onChange={(e) => setDraft({ ...draft, stopConditions: e.target.value })}
              className={MONO_FIELD}
            />
          </Row>

          <Row label="ai_os.field.rollback" hint="ai_os.field.rollback.hint">
            <textarea
              data-testid="ai-os-create-rollback"
              value={draft.rollback}
              rows={2}
              onChange={(e) => setDraft({ ...draft, rollback: e.target.value })}
              className={MONO_FIELD}
            />
          </Row>

          <Row label="ai_os.field.budget" hint="ai_os.field.budget.hint">
            <textarea
              data-testid="ai-os-create-budget"
              value={draft.budget}
              rows={2}
              onChange={(e) => setDraft({ ...draft, budget: e.target.value })}
              className={MONO_FIELD}
            />
          </Row>

          <Row label="ai_os.field.risk_level">
            <div className="flex flex-col gap-npc-gap-xs">
              {ALL_RISKS.map((risk) => (
                <label key={risk} className="inline-flex items-start gap-npc-gap-sm">
                  <input
                    type="radio"
                    name="ai-os-risk"
                    className="mt-[5px]"
                    data-testid={`ai-os-create-risk-${risk}`}
                    checked={draft.riskLevel === risk}
                    onChange={() =>
                      setDraft({
                        ...draft,
                        riskLevel: risk,
                        // 红级一律要人点。选到红的那一刻就勾上并锁死，
                        // 而不是等提交回 400 才说
                        requiresHumanClick: riskRequiresHumanClick(risk)
                          ? true
                          : draft.requiresHumanClick,
                      })
                    }
                  />
                  <span className="min-w-0">
                    <RiskTag risk={risk} />
                    {/* 三档各自那一句解释。**选之前就看得见**——
                        「这件事有多大」是他最容易选错的一项，
                        而选错的代价是红级被填成绿级 */}
                    <span className="ml-npc-gap-xs text-npc-meta leading-[1.6] text-npc-ink-softer">
                      <T id={RISK_DETAIL[risk]} />
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </Row>

          <Row label="ai_os.field.owner_stream">
            <input
              data-testid="ai-os-create-owner"
              value={draft.ownerStream}
              maxLength={64}
              onChange={(e) => setDraft({ ...draft, ownerStream: e.target.value })}
              className={FIELD}
            />
          </Row>

          <Row label="ai_os.field.forbidden_boundaries">
            <label className="flex items-start gap-npc-gap-sm">
              <input
                type="checkbox"
                data-testid="ai-os-create-human-click"
                checked={draft.requiresHumanClick}
                disabled={riskRequiresHumanClick(draft.riskLevel)}
                onChange={(e) => setDraft({ ...draft, requiresHumanClick: e.target.checked })}
                className="mt-[3px]"
              />
              <span className="text-npc-caption leading-[1.6] text-npc-ink-medium">
                <T id="ai_os.field.requires_human_click" />
                {riskRequiresHumanClick(draft.riskLevel) ? (
                  <span data-testid="ai-os-create-red-locked" className="ml-npc-gap-xs text-npc-amber-ink">
                    <T id="ai_os.field.requires_human_click.locked" />
                  </span>
                ) : null}
              </span>
            </label>
          </Row>

          {invalid ? (
            <p
              data-testid="ai-os-create-invalid"
              className="m-0 rounded-npc-inset border border-npc-amber-border bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink"
            >
              <T id={invalid} />
            </p>
          ) : null}

          {pending ? (
            <p data-testid="ai-os-create-submitting" className="m-0 text-npc-caption text-npc-ink-soft">
              <T id="ai_os.loading.submitting" />
            </p>
          ) : null}

          {replayed && !error ? (
            <p data-testid="ai-os-create-replayed" className="m-0 text-npc-caption text-npc-ink-soft">
              <T id="ai_os.conflict.already_submitted" />
            </p>
          ) : null}

          {error !== undefined && error !== null && !invalid ? (
            <p
              data-testid={conflict ? 'ai-os-create-conflict' : 'ai-os-create-error'}
              className="m-0 rounded-npc-inset border border-npc-red-border bg-npc-red-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-red-ink"
            >
              {conflict ? (
                <T id="ai_os.conflict.same_key_different_content" />
              ) : moduleGone ? (
                <T id="ai_os.error.module_gone" />
              ) : redRefused(error) ? (
                <T id="ai_os.error.red_needs_click" />
              ) : error instanceof ApiFailure && error.kind === 'reported' && error.message ? (
                /* 后端按契约说了话就用它那句——它比这边这条泛用的准 */
                error.message
              ) : (
                /* 「你刚填的还在，不用重打」——这句在这儿是真的：
                    弹窗没关，draft 还在 state 里。
                    留给「后端根本没说话」那一档 */
                <T id="ai_os.error.create_failed" />
              )}
            </p>
          ) : null}
        </form>
      </Modal>
    </>
  )
}

/**
 * 服务端拒的是「红级配不用人点」吗。
 *
 * 判据是 `code` 不是状态码，也不是那句话的文字：状态码两类共用，文案会改，
 * **`code` 是契约**（与 `app/errors.ts` 判 `step_up_required` 同一条理由）。
 */
function redRefused(error: unknown): boolean {
  return error instanceof ApiFailure && error.code === 'red_requires_human_click'
}

const FIELD =
  'w-full rounded-npc-inset border border-npc-border-soft bg-npc-panel px-npc-gap-sm py-npc-gap-xs text-npc-body-sm text-npc-ink'
const MONO_FIELD = `${FIELD} font-npc-mono text-npc-meta-sm`

/**
 * 「影响的是谁」那个框。
 *
 * 没有字段名模板（见调用点那段注释），所以这里用**边填边显示**顶上：
 * 值非空时下面渲染 `ai_os.proposal.impact`，那句话既是预览也是标签。
 *
 * 值不干净时（空、控制符、超 200 字、emoji、术语黑名单）`render` 会抛，
 * 所以先用 `checkValue` 问一句——与 `view.tsx` 的 `Benefit` 同一条理由：
 * **一条写得太长的输入不该让整个弹窗炸掉。**
 */
function ImpactField({
  value,
  onChange,
}: {
  value: string
  onChange: (next: string) => void
}) {
  const clean = value.trim() !== '' && checkValue('who', value) === null

  return (
    <div className="flex flex-col gap-npc-gap-xs">
      <input
        data-testid="ai-os-create-impact"
        value={value}
        aria-describedby="ai-os-impact-preview"
        onChange={(e) => onChange(e.target.value)}
        className={FIELD}
      />
      <p id="ai-os-impact-preview" className="m-0 text-npc-meta leading-[1.6] text-npc-ink-softer">
        {clean ? (
          <span data-testid="ai-os-create-impact-preview">
            <T id="ai_os.proposal.impact" vars={{ who: value.trim() }} />
          </span>
        ) : (
          <T id="ai_os.field.user_value.hint" />
        )}
      </p>
    </div>
  )
}

function Row({
  label,
  hint,
  children,
}: {
  label: CopyId
  hint?: CopyId
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-npc-gap-xs">
      <div className="text-npc-caption font-medium text-npc-ink">
        <T id={label} />
      </div>
      {hint ? (
        <p className="m-0 text-npc-meta leading-[1.6] text-npc-ink-softer">
          <T id={hint} />
        </p>
      ) : null}
      {children}
    </div>
  )
}
